import React, {FC, ReactNode} from 'react';
import classNames from 'classnames';
import {
  Button,
  DocumentGradientIcon,
  GreaterThanIcon,
  SuitcaseIcon,
} from 'components';
import dayjs from 'dayjs';
import {AudioIcon} from 'features/Provider/Experiences/WellniteAudios/Icons/AudioIcon';
import {LazyImage} from 'hooks';
import i18next from 'i18next';
import {Audio, UnsplashImage, WellniteContentArticle} from 'interfaces';
import {useTranslation} from 'react-i18next';

import {SelectOptions} from '../useDirectBookingFilter';
import {TProfessionalHeadshot} from '../utils';

import {TextLabel} from './DirectBookingLabel';

type Props = {
  name: string;
  licences: string[];
  states?: string[];
  slim?: boolean;
  headshot: TProfessionalHeadshot;
  conditions: string[];
  acceptedInsurances: string[];
  article?: WellniteContentArticle;
  audio?: Audio;
  nextAvailable?: string;
  bio?: string;
  selectedOptions?: SelectOptions;
};

const renderLabels = (
  items: string[],
  selectedItems: string[] = [],
  keyPrefix: string,
  maxToShow: number = 8,
) => {
  const uniqueItems = Array.from(new Set(items));
  const sortedItems = uniqueItems.sort(
    (a, b) =>
      Number(selectedItems.includes(b)) - Number(selectedItems.includes(a)),
  );

  const showItems = sortedItems.slice(0, maxToShow);
  const hasMore = sortedItems.length > maxToShow;

  return [
    ...showItems.map(item => (
      <TextLabel
        key={`${keyPrefix}-${item}`}
        text={item}
        selected={selectedItems.includes(item)}
        selectedColor="!bg-purple-100"
        bgColor="bg-primary/[.05]"
      />
    )),
    hasMore ? (
      <span key={`${keyPrefix}-more`} className="text-sm text-gray-500 ml-2">
        {i18next.t('directory.more...')}
      </span>
    ) : null,
  ];
};

const InfoCard: FC<{
  title: string;
  icon: ReactNode;
  article?: WellniteContentArticle | Audio;
  type: 'article' | 'audio';
  bgColor: string;
  emptyText: string;
}> = ({title, icon, article, type, bgColor, emptyText}) => {
  const imageUrl =
    type === 'article'
      ? (
          (article as WellniteContentArticle)?.content?.find(
            c => c.type === 'image',
          )?.value as UnsplashImage
        )?.urls?.thumb
      : (article as Audio)?.coverImageThumbnailUrl;

  return (
    <div
      className={classNames(
        'w-48 h-60 rounded-3xl p-3 flex flex-col gap-4',
        bgColor,
      )}
    >
      <div className="flex gap-1 justify-between items-center">
        <div className="p-2 rounded-xl bg-white flex items-center justify-center">
          {icon}
        </div>
        <span className="font-semibold">{title}</span>
        <GreaterThanIcon />
      </div>
      <div className="relative rounded-2xl overflow-hidden h-40 w-full bg-white flex items-center justify-center">
        {article && imageUrl ? (
          <>
            <img
              className="object-cover w-full h-full rounded-2xl"
              src={imageUrl}
              alt={article.title}
            />
            <div className="absolute bottom-0 left-0 w-full bg-black/50 text-white text-xs px-2 py-1">
              {article.title}
            </div>
          </>
        ) : (
          <span className="text-gray-600 text-sm">{emptyText}</span>
        )}
      </div>
    </div>
  );
};

const DirectBookingProviderCard: FC<Props> = ({
  headshot,
  name,
  licences,
  conditions,
  acceptedInsurances,
  article,
  bio,
  audio,
  nextAvailable,
  selectedOptions,
}) => {
  const {t} = useTranslation();
  const nextAvailableDate = nextAvailable
    ? dayjs(new Date(nextAvailable)).format('DD MMMM')
    : '';
  return (
    <section className="max-w-7xl mx-auto sm:px-6 lg:px-8 py-4">
      <div className="w-full flex flex-col lg:flex-row items-start justify-between">
        <div className="flex items-center mb-4 md:mb-0">
          <LazyImage
            src={headshot.medium}
            srcSet={`${headshot.medium} ${headshot.large} 3x`}
            alt={t('directory.professionalHeadshot')}
            lqip={headshot.small}
            type="image/png"
            aspectRatio={1}
            className="rounded-full box-content border-[#ffcb58]/[.15] object-cover w-24 h-24 md:w-[167px] md:h-[167px] mr-4"
          />
          <div className="flex flex-col">
            <span className="font-montserrat leading-6 font-semibold text-xl">
              {name}
            </span>
            {licences.length > 0 ? (
              <TextLabel
                text={licences.join(', ')}
                bgColor="bg-white !px-0 !m-0"
                icon={
                  <SuitcaseIcon
                    height={18}
                    classes="flex-shrink-0 self-center"
                  />
                }
              />
            ) : null}
          </div>
        </div>
        <div className="w-full lg:w-auto flex flex-col items-center lg:flex-row gap-4">
          <span className="text-sm text-center">
            {t('directory.nextAvailable')}&nbsp;{nextAvailableDate}
          </span>
          <Button
            btnType="black"
            className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-3xl hover:bg-blue-700 transition duration-300"
          >
            {t('bookAppointment')}
          </Button>
        </div>
      </div>

      <div className="flex gap-8 justify-between pt-10 flex-col lg:flex-row">
        <div className="w-full hidden md:flex flex-col gap-4">
          <p className="line-clamp-4">{bio}</p>

          <div className="bg-zinc-50 rounded-3xl p-4">
            <p className="mb-4">{t('directory.insurances')}</p>
            <div className="font-sm mb-4 flex items-center flex-wrap">
              {acceptedInsurances.length > 0
                ? renderLabels(
                    acceptedInsurances,
                    selectedOptions?.acceptedInsurances,
                    'insurance',
                  )
                : t('directory.noInsurances')}
            </div>
          </div>

          <div className="bg-zinc-50 rounded-3xl p-4">
            <p className="mb-4">Conditions</p>
            <div className="font-sm mb-4 flex items-center flex-wrap">
              {conditions.length > 0
                ? renderLabels(
                    conditions,
                    selectedOptions?.conditions,
                    'condition',
                  )
                : t('directory.noConditions')}
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <InfoCard
            title={t('directory.myAudios')}
            icon={<AudioIcon width={20} height={20} />}
            article={audio}
            bgColor={audio ? 'bg-pink-100' : 'bg-gray-200'}
            emptyText={t('directory.noAudiosCreated')}
            type="audio"
          />
          <InfoCard
            title={t('directory.myArticles')}
            icon={<DocumentGradientIcon width={20} height={20} />}
            article={article}
            bgColor={article ? 'bg-[#FFF3D8]' : 'bg-gray-200'}
            emptyText={t('directory.noArticlesCreated')}
            type="article"
          />
        </div>
      </div>
    </section>
  );
};

export default DirectBookingProviderCard;
